import { FhirBundleSdk } from "@metriport/fhir-sdk";
import { ISO_DATE } from "@metriport/shared/common/date";
import dayjs from "dayjs";
import {
  getObservationCode as getObservationCodeForGrouping,
  getObservationDate,
  groupResourcesByKey,
  ObservationSeries,
} from "../../table-transforms/series-utils";
import { getObservationValue } from "../../utils/observation";

type ChartSeries = {
  name: string;
  data: Array<{ date: string; value: number }>;
  color?: string;
};

export type SurveyRowData = {
  id: string;
  observation: string;
  mostRecentValue: string;
  mostRecentDate: string;
  originalData: ObservationSeries;
  chartSeries: ChartSeries[];
};

function extractObservationData(series: ObservationSeries): Array<{ date: string; value: number }> {
  const data: Array<{ date: string; value: number }> = [];

  for (const resource of series.resources) {
    const date = getObservationDate(resource);
    if (!date || date === "-") continue;

    const value = resource.valueQuantity?.value;
    if (value === undefined) continue;

    data.push({ date, value });
  }

  return data;
}

export function getSurveyRows({ bundle }: { bundle: FhirBundleSdk }): SurveyRowData[] {
  // Get only survey observations (category = "survey")
  const observations = bundle.getObservations().filter(observation => {
    return (
      observation.category?.some(category =>
        category.coding?.some(coding => coding.code?.toLowerCase() === "survey")
      ) ?? false
    );
  });

  // Group observations by code into series
  const observationSeries = groupResourcesByKey(
    observations,
    getObservationCodeForGrouping,
    getObservationDate,
    "Observation"
  );

  // Convert series to row data
  return observationSeries.map(series => {
    const latestObservation = series.resources[series.resources.length - 1]; // Last in array is newest

    if (!latestObservation) {
      throw new Error("Series should always have at least one resource");
    }

    const observationName =
      latestObservation.code?.coding?.[0]?.display || latestObservation.code?.text || "-";

    return {
      id: `series-${series.code}`,
      observation: observationName,
      mostRecentValue: getObservationValue(latestObservation),
      mostRecentDate: formatDate(series.latestDate),
      originalData: series,
      chartSeries: [
        {
          name: observationName,
          data: extractObservationData(series),
        },
      ],
    };
  });
}

function formatDate(date: string | undefined): string {
  if (!date || date === "-") return "-";

  const dayjsDate = dayjs(date);
  if (!dayjsDate.isValid()) return "-";

  return dayjsDate.format(ISO_DATE);
}
